import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import { Store } from 'src/store';
import getForwardUrl from 'src/store/app/selector/getForwardUrl';
import goToOptimalprintOperation from 'src/store/app/operation/goToOptimalprintOperation';
import hidePopupOperation from 'src/store/app/operation/hidePopupOperation';
import showPopupOperation from 'src/store/app/operation/showPopupOperation';
import { showPopupAction } from 'src/store/app/slice';
import sendEmptyProductWarningOperation from 'src/store/app/operation/sendEmptyProductWarningOperation';
import { Feature, EDITOR_CROSSSELL_POPUP_ID } from 'src/store/app/types';
import updateCustomerDesignOperation from 'src/store/design/operation/updateCustomerDesignOperation';
import getDesignId from 'src/store/design/selector/getDesignId';
import getProductUid from 'src/store/design/selector/getProductUid';
import getStructure from 'src/store/design/selector/getStructure';
import isDesignSaving from 'src/store/design/selector/isDesignSaving';
import shouldDisplayLowDPIWarning from 'src/store/design/selector/shouldDisplayLowDPIWarning';
import { setDesignIsLoadingAction, setDesignIsSavingAction, setDesignOrderItemIdAction } from 'src/store/design/slice';
import trackEventOperation from 'src/store/addon/operation/trackEventOperation';
import { clearMultiSizePendingItemsAction } from 'src/store/multisize/slice';
import isDesignHasEmptyContent from 'src/util/design/isDesignHasEmptyContent';
import isFeatureEnabled from 'src/util/feature/isFeatureEnabled';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';

/**
 * OP-39782: commit a multi-size apparel selection as one grouped basket line.
 *
 * Mirrors the single-add pre-commit chain (empty / low-DPI guards + design save)
 * then commits via the personalised batch endpoint (OP-39659), which clones the
 * base design per size and adds each as an order item, and redirects to basket.
 *
 * v1 scope: no bundled products, no DPI/hanger/remove-logo add-ons and no
 * pre-basket upsell — the batch endpoint carries only {productId, quantity}.
 */
const multiSizeAddToBasketOperation = () => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
  const store = getState();
  const items = store.multisize.pendingItems;
  const designData = getStructure(store);

  if (isDesignSaving(store) || items.length === 0) {
    return;
  }

  if (isFeatureEnabled(Feature.BlockPopupWhenEmptyDesign) && designData && isDesignHasEmptyContent(designData)) {
    dispatch(sendEmptyProductWarningOperation());
    return;
  }

  dispatch(hidePopupOperation());

  if (isDesignHasEmptyContent(designData)) {
    dispatch(trackEventOperation('customerAttemptAddToCartEmptyDesign'));
  }

  if (shouldDisplayLowDPIWarning(store)) {
    dispatch(showPopupOperation('LowDPIWarningPopup'));
    return;
  }

  try {
    dispatch(setDesignIsLoadingAction(true));
    dispatch(setDesignIsSavingAction(true));

    const designUpdateResult = await dispatch(updateCustomerDesignOperation());
    if (!designUpdateResult) {
      dispatch(showPopupOperation('UpdateDesignWarningPopup'));
      dispatch(trackEventOperation('customerDesignUpdateFailed'));
      throw new Error('Error updating customer design');
    }

    const batchResult = (await opApiRequest(
      endpoints.CART_V1_DESIGN_BATCH_ADD,
      { customerDesignId: getDesignId(store), items },
      'data',
      'POST',
    )) as { items?: Array<{ orderItemId?: number }> };

    // Keep one of the newly-created order items so the cross-sell popup can scope
    // its personalised previews to the design just added (the batch clones the
    // design per size, so the original design id no longer maps to an order item).
    const addedOrderItemId = batchResult?.items?.[0]?.orderItemId;
    if (addedOrderItemId) {
      dispatch(setDesignOrderItemIdAction(addedOrderItemId));
    }

    dispatch(clearMultiSizePendingItemsAction());

    const { categoryName, isApparel } = useIntegrationLayer();
    window.dataLayer.push({
      event: 'editorAddToBasket',
      category: categoryName,
      product: getProductUid(store),
      editorVersion: 'New',
      addMode: 'multisize',
    });

    // Post-add cross-sell: apparel-only, terminal surface before the cart.
    if (isApparel && isFeatureEnabled(Feature.editorCrossSell) && getDesignId(getState())) {
      // Open without a backdrop: the popup mounts hidden and prefetches, then
      // reveals itself (with the backdrop) only if it has items to show.
      dispatch(showPopupAction(EDITOR_CROSSSELL_POPUP_ID));
      dispatch(setDesignIsLoadingAction(false));
      dispatch(setDesignIsSavingAction(false));
      return;
    }

    dispatch(goToOptimalprintOperation(getForwardUrl(store)));
  } catch (e) {
    dispatch(setDesignIsLoadingAction(false));
    dispatch(setDesignIsSavingAction(false));
    // eslint-disable-next-line no-console
    console.error('Cannot add multi-size design to basket', e);
  }
};

export default multiSizeAddToBasketOperation;
